import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type Home as HomeData } from "../api";
import { ErrorNotice, Loading, Menu, useFilePicker } from "../ui";
import { useUpload } from "./upload";

export function Home() {
  const [home, setHome] = useState<HomeData | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { upload, busy, error: uploadError } = useUpload();
  const navigate = useNavigate();
  const pick = useFilePicker(upload);

  useEffect(() => {
    api.get<HomeData>("/api/home").then(setHome).catch(setError);
  }, []);

  if (!home) return <><ErrorNotice error={error} /><Loading /></>;
  const firstName = home.name.split(" ")[0];

  return (
    <div className="stack" style={{ "--gap": "28px" } as React.CSSProperties}>
      <div className="row wrap">
        <div>
          <h1>Hi {firstName}</h1>
          <p className="muted">{home.year.label} financial year</p>
        </div>
        <div className="spacer" />
        <Menu primary label={busy ? "Uploading…" : "+ Add"}>
          <button type="button" onClick={pick}>Upload a receipt or document</button>
          <button type="button" disabled>Scan with iPhone <span className="pill">Soon</span></button>
          <button type="button" onClick={() => navigate("/cars/new")}>Add a car</button>
          <button type="button" onClick={() => navigate("/properties/new")}>Add a property</button>
          <button type="button" onClick={() => navigate("/work")}>Add a job</button>
          <button type="button" disabled>Something changed… <span className="pill">Soon</span></button>
        </Menu>
      </div>
      <ErrorNotice error={uploadError} />

      <div className="grid">
        <Link to="/inbox" className="card" style={{ textDecoration: "none", color: "inherit" }}>
          <div className="stat">
            <span className="muted small">To review</span>
            <strong style={{ fontSize: 28 }}>{home.toReview}</strong>
            <span className="muted small">{home.toReview === 0 ? "You're all caught up" : "Receipts waiting for a quick check"}</span>
          </div>
        </Link>
        {home.documents && (
          <Link to={`/documents?fy=${home.documents.fy}`} className="card" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="stat">
              <span className="muted small">Documents for {home.documents.label}</span>
              <strong style={{ fontSize: 28 }}>{home.documents.have} of {home.documents.total}</strong>
              <span className="muted small">{home.documents.have === home.documents.total ? "Everything's in" : "See what's still to come"}</span>
            </div>
          </Link>
        )}
        <div className="card">
          <div className="stat">
            <span className="muted small">Estimated refund or payable</span>
            <strong style={{ fontSize: 18 }}>Not enough yet</strong>
            <span className="muted small">Appears once your income is recorded.</span>
          </div>
        </div>
      </div>

      {home.tasks.length > 0 && (
        <section className="stack">
          <h2>To do</h2>
          <div className="card list-card list">
            {home.tasks.map((t) => (
              <div className="list-item" key={t.id}>
                <span aria-hidden>○</span>
                <span>{t.title}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="stack">
        <h2>Finish setting up</h2>
        {home.setupCards.length === 0 ? (
          <div className="card flat empty">Nothing to set up. You can turn on more areas in settings later.</div>
        ) : (
          <div className="grid">
            {home.setupCards.map((c) => (
              <div className="card stack" key={c.area} style={{ "--gap": "8px" } as React.CSSProperties}>
                <div className="row">
                  <h3>{c.title}</h3>
                  <div className="spacer" />
                  {c.status === "coming_soon" && <span className="pill">Coming soon</span>}
                  {c.status === "done" && <span className="pill ok">✓ {c.doneText}</span>}
                </div>
                <p className="muted small">{c.description}</p>
                {c.action === "add_vehicle" && (
                  c.status === "done"
                    ? <Link to="/cars" className="btn" style={{ alignSelf: "flex-start" }}>View cars</Link>
                    : <Link to="/cars/new" className="btn primary" style={{ alignSelf: "flex-start" }}>Add your car</Link>
                )}
                {(c.action === "add_job" || c.action === "set_home_office") && (
                  <Link to="/work" className={`btn${c.status === "done" ? "" : " primary"}`} style={{ alignSelf: "flex-start" }}>
                    {c.status === "done" ? "View work" : c.action === "add_job" ? "Add your job" : "Set up working from home"}
                  </Link>
                )}
                {c.action === "add_property" && (
                  c.status === "done"
                    ? <Link to="/properties" className="btn" style={{ alignSelf: "flex-start" }}>View properties</Link>
                    : <Link to="/properties/new" className="btn primary" style={{ alignSelf: "flex-start" }}>{c.area === "own_home" ? "Add your home" : "Add your property"}</Link>
                )}
              </div>
            ))}
          </div>
        )}
        {home.setupCards.some((c) => c.status === "coming_soon") && <p className="muted small">Set-up for the others arrives in an upcoming update. Receipts you upload now are kept and can be linked later.</p>}
      </section>
    </div>
  );
}
